// apps/client/src/pages/leads/edm/EdmContactsPage.tsx
//
// /leads/edm/contacts (eDM Phase 1c S1 + S5): the company's eDM contacts.
// Company-wide callers only (RequireEdmImportAccess on the route, the
// edmContactAction gate on the server). Reads only through edmContactAction;
// no Firestore SDK reads. There is deliberately no export, copy-all or
// download control on this page (VALUTTEN never exports tenant data).
//
// With `featureFlags.edm_content` on (plan-v3 core E2): the list pages by
// server cursor whenever no status/search filter is set (Previous / Next,
// no page jumps), and the tag filter groups tags by kind. Flag absent or a
// status/search filter set: the Phase 1c offset paging and flat tag list.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ChevronLeft, ChevronRight, History, Pencil, Plus, Tags, Trash2, Users } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { SkeletonTablePage } from '../../../components/common/SkeletonLoader';
import { Pagination } from '../../../components/common/Pagination';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Modal from '../../../components/common/Modal';
import { DatePickerField } from '../../../components/common/DatePickerField';
import {
  EDM_CONTACTS_MAX_PAGE_SIZE,
  edmContactErrorMessage,
  isContactsCursorMode,
  useEdmBrokerLabels,
  useEdmContacts,
  type AddContactInput,
  type EdmContact,
  type EdmContactFilters,
  type EdmContactStatusFilter,
  type EdmTagEntry,
  type EdmTagKind,
  type UpdateContactInput,
} from '../../../hooks/useEdmContacts';
import { EDM_UI, formatBrisbaneDateTime, isEdmContentEnabled } from '../../../utils/edm';
import ContactFormModal from './components/ContactFormModal';
import ContactHistoryDrawer from './components/ContactHistoryDrawer';
import BulkTagBar from './components/BulkTagBar';

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

const TAG_KIND_LABEL: Record<EdmTagKind, string> = { tag: 'Tag', category: 'Category', event: 'Event' };

/** Optgroup order and headings for the kind-grouped tag filter. */
const TAG_KIND_ORDER: readonly EdmTagKind[] = ['tag', 'category', 'event'];
const TAG_KIND_GROUP_LABEL: Record<EdmTagKind, string> = { tag: 'Tags', category: 'Categories', event: 'Events' };

/** Tags grouped by kind (registry order kept within a group); empty groups dropped. */
export function groupTagsByKind(tags: EdmTagEntry[]): Array<{ kind: EdmTagKind; tags: EdmTagEntry[] }> {
  return TAG_KIND_ORDER.map((kind) => ({
    kind,
    // An entry with an unknown kind is shown with the plain tags, never dropped.
    tags: tags.filter((t) => (TAG_KIND_ORDER.includes(t.kind) ? t.kind : 'tag') === kind),
  })).filter((g) => g.tags.length > 0);
}

interface CursorPagerProps {
  page: number;
  pageSize: number;
  shown: number;
  total: number;
  hasNext: boolean;
  busy: boolean;
  onPrev: () => void;
  onNext: () => void;
  onPageSizeChange: (n: number) => void;
}

/** Previous / Next pager for server cursor pages (no page jumps). */
const CursorPager: React.FC<CursorPagerProps> = ({
  page,
  pageSize,
  shown,
  total,
  hasNext,
  busy,
  onPrev,
  onNext,
  onPageSizeChange,
}) => {
  const start = shown === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = (page - 1) * pageSize + shown;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800 px-4 py-3 sm:px-6"
      aria-label="Contact pages"
    >
      <div className="flex flex-wrap items-center gap-4">
        <p className="text-sm text-gray-400">
          Showing <span className="font-medium text-gray-100">{start.toLocaleString('en-AU')}</span> to{' '}
          <span className="font-medium text-gray-100">{end.toLocaleString('en-AU')}</span> of{' '}
          <span className="font-medium text-gray-100">{total.toLocaleString('en-AU')}</span> contacts
        </p>
        <div className="flex items-center gap-2">
          <label htmlFor="edm-contacts-page-size" className="text-sm text-gray-400">
            Rows per page:
          </label>
          <select
            id="edm-contacts-page-size"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border border-gray-600 bg-gray-700 px-3 py-1.5 text-sm text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" className={EDM_UI.secondaryButton} disabled={busy || page <= 1} onClick={onPrev}>
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <span className="text-sm text-gray-400">Page {page.toLocaleString('en-AU')}</span>
        <button type="button" className={EDM_UI.secondaryButton} disabled={busy || !hasNext} onClick={onNext}>
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

/** Subscription / suppression state in words. */
export function contactStateLabel(c: EdmContact): string {
  if (c.suppression) {
    switch (c.suppression.reason) {
      case 'bounced':
      case 'bounce':
        return 'Bounced';
      case 'complained':
      case 'complaint':
        return 'Complained';
      case 'manual_erasure':
      case 'erasure_request':
        return 'Erased';
      case 'unsubscribed':
      case 'unsubscribe':
      // What the unsubscribe link and a staff-recorded unsubscribe write
      // (email/unsubscribeHandler.ts applySuppression).
      case 'manual_unsubscribe':
        return 'Unsubscribed';
      default:
        return 'Suppressed';
    }
  }
  return c.subscribed ? 'Subscribed' : 'Unsubscribed';
}

const fullName = (c: EdmContact) => [c.firstName, c.lastName].filter(Boolean).join(' ');

// ---------------------------------------------------------------- tag registry

interface TagManagerProps {
  tags: EdmTagEntry[];
  busy: boolean;
  error: string | null;
  onSave: (name: string, kind: EdmTagKind, eventDate: string | null) => void;
  onDelete: (name: string) => void;
}

const TagManager: React.FC<TagManagerProps> = ({ tags, busy, error, onSave, onDelete }) => {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<EdmTagKind>('tag');
  const [eventDate, setEventDate] = useState('');
  const trimmed = name.trim();
  const canSave = !busy && trimmed.length > 0 && trimmed.length <= 60;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">
        Tags group contacts so a campaign can target them. Deleting a tag removes it from this list only; contacts
        keep it.
      </p>
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        aria-label="New tag"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSave) return;
          onSave(trimmed, kind, kind === 'event' && eventDate ? eventDate : null);
          setName('');
          setEventDate('');
        }}
      >
        <div>
          <label className={EDM_UI.label} htmlFor="edm-tag-name">
            Name
          </label>
          <input
            id="edm-tag-name"
            className={EDM_UI.input}
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-tag-kind">
            Kind
          </label>
          <select
            id="edm-tag-kind"
            className={EDM_UI.input}
            value={kind}
            onChange={(e) => setKind(e.target.value as EdmTagKind)}
          >
            <option value="tag">Tag</option>
            <option value="category">Category</option>
            <option value="event">Event</option>
          </select>
        </div>
        {kind === 'event' && (
          <div>
            <DatePickerField label="Event date" value={eventDate || null} onChange={setEventDate} />
          </div>
        )}
        <div className="sm:col-span-3">
          <button type="submit" className={EDM_UI.primaryButton} disabled={!canSave}>
            <Plus className="h-4 w-4" /> Save tag
          </button>
        </div>
      </form>

      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      {tags.length === 0 ? (
        <p className="text-sm text-gray-400">No tags yet.</p>
      ) : (
        <ul className="divide-y divide-gray-700 rounded-lg border border-gray-700">
          {tags.map((t) => (
            <li key={t.name} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <span className="text-gray-100">{t.name}</span>
              <span className="flex items-center gap-3 text-xs text-gray-400">
                <span>{TAG_KIND_LABEL[t.kind] ?? t.kind}</span>
                {t.eventDate && <span>{t.eventDate}</span>}
                {t.registered === false ? (
                  <span title="On imported contacts; not yet saved as a tag">Not saved</span>
                ) : (
                  <button
                    type="button"
                    className="text-red-400 hover:text-red-300 disabled:opacity-50"
                    disabled={busy}
                    aria-label={`Delete tag ${t.name}`}
                    onClick={() => onDelete(t.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- page

const EdmContactsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx?.userData?.companyId;
  const contentEnabled = isEdmContentEnabled(ctx?.companyData?.featureFlags);
  const api = useEdmContacts(companyId);
  const brokers = useEdmBrokerLabels(companyId);
  const brokerName = useCallback(
    (id: string | null) => (id ? brokers.find((b) => b.id === id)?.displayName ?? '–' : '–'),
    [brokers],
  );

  // Filters. The search box is debounced into `filters.q`.
  const [search, setSearch] = useState('');
  const [importDraft, setImportDraft] = useState('');
  const [filters, setFilters] = useState<EdmContactFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [contacts, setContacts] = useState<EdmContact[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Cursor mode only (edm_content on, no status/search filter):
  // cursorsRef.current[i] is the cursor that loads page i + 1 ('' = page 1);
  // nextCursor is the server's cursor after the current page (null = last).
  const cursorsRef = useRef<string[]>(['']);
  const requestSeq = useRef(0);
  // The cursor that last returned an empty page (its contacts were erased
  // after it was handed out). A reloaded previous page that hands the same
  // cursor back is treated as the last page, so Next cannot loop.
  const emptyCursorRef = useRef<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const cursorMode = isContactsCursorMode(contentEnabled, filters);

  const [tags, setTags] = useState<EdmTagEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EdmContact | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<EdmContact | null>(null);
  const [historyContact, setHistoryContact] = useState<EdmContact | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [tagsOpen, setTagsOpen] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [tagDeleteTarget, setTagDeleteTarget] = useState<string | null>(null);

  const currentQ = filters.q;
  useEffect(() => {
    const q = search.trim() || undefined;
    if (q === currentQ) return undefined;
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, q }));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, currentQ]);

  const load = useCallback(() => {
    if (cursorMode) {
      // Page 1 (first load, any filter or page-size change) restarts the chain.
      if (page === 1) cursorsRef.current = [''];
      const cursor = cursorsRef.current[page - 1];
      if (cursor === undefined) {
        // Unreachable through the pager; never request a page with no cursor.
        setPage(1);
        return;
      }
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      setSelected(new Set());
      api
        .listContactsPage(filters, cursor, pageSize)
        .then((res) => {
          if (seq !== requestSeq.current) return;
          if (page > 1 && (res.contacts ?? []).length === 0) {
            // The page emptied (contacts erased since it was cursored): step
            // back rather than strand the user on a page with no pager.
            emptyCursorRef.current = cursor;
            setPage(page - 1);
            return;
          }
          setContacts(res.contacts ?? []);
          setTotal(res.total ?? 0);
          const raw = typeof res.nextCursor === 'string' && res.nextCursor !== '' ? res.nextCursor : null;
          const next = raw !== null && raw === emptyCursorRef.current ? null : raw;
          setNextCursor(next);
          cursorsRef.current = cursorsRef.current.slice(0, page);
          if (next !== null) cursorsRef.current[page] = next;
        })
        .catch((err) => {
          if (seq === requestSeq.current) setError(edmContactErrorMessage(err));
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false);
        });
      return;
    }
    // Latest request wins in both modes, so a slow response for an earlier
    // filter (or the other paging mode) never overwrites the current page.
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    api
      .listContacts(filters, page, pageSize)
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setContacts(res.contacts ?? []);
        setTotal(res.total ?? 0);
      })
      .catch((err) => {
        if (seq === requestSeq.current) setError(edmContactErrorMessage(err));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [api, filters, page, pageSize, cursorMode]);

  // A new filter or page size is a new cursor chain. Declared before the load
  // effect so it runs first.
  useEffect(() => {
    emptyCursorRef.current = null;
  }, [filters, pageSize, cursorMode]);

  useEffect(() => {
    load();
  }, [load]);

  const loadTags = useCallback(() => {
    api
      .listTags()
      .then((res) => setTags(res.tags ?? []))
      .catch((err) => setTagError(edmContactErrorMessage(err)));
  }, [api]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  const setFilter = <K extends keyof EdmContactFilters>(k: K, v: EdmContactFilters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const applyImportId = () => {
    const v = importDraft.trim() || undefined;
    if (v !== filters.importId) setFilter('importId', v);
  };

  // ---- add / edit
  const openAdd = () => {
    setEditing(null);
    setFormError(null);
    setFormOpen(true);
  };
  const openEdit = (c: EdmContact) => {
    setEditing(c);
    setFormError(null);
    setFormOpen(true);
  };
  const closeForm = () => {
    if (formBusy) return;
    setFormOpen(false);
    setEditing(null);
  };
  const runForm = (p: Promise<unknown>, done: string) => {
    setFormBusy(true);
    setFormError(null);
    p.then(() => {
      setFormOpen(false);
      setEditing(null);
      setNotice(done);
      load();
    })
      .catch((err) => setFormError(edmContactErrorMessage(err)))
      .finally(() => setFormBusy(false));
  };
  const onAdd = (input: AddContactInput) => runForm(api.addContact(input), `Added ${input.email}.`);
  const onUpdate = (input: UpdateContactInput) => runForm(api.updateContact(input), 'Contact updated.');

  // ---- erase
  const confirmDelete = () => {
    const target = deleteTarget;
    if (!target) return;
    setDeleteTarget(null);
    api
      .deleteContact(target.id)
      .then(() => {
        setNotice(`Erased ${target.email}. The address is permanently suppressed.`);
        load();
      })
      .catch((err) => setError(edmContactErrorMessage(err)));
  };

  // ---- selection + bulk tag
  const pageIds = (contacts ?? []).map((c) => c.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(pageIds));
  const applyBulk = (add: string[], remove: string[]) => {
    const ids = Array.from(selected);
    setBulkBusy(true);
    api
      .bulkTag(ids, add, remove)
      .then((result) => {
        const updated = typeof result.updated === 'number' ? result.updated : ids.length;
        const failed = typeof result.failed === 'number' ? result.failed : 0;
        const done =
          add.length > 0
            ? `Tagged ${updated.toLocaleString('en-AU')} contacts "${add[0]}".`
            : `Removed "${remove[0]}" from ${updated.toLocaleString('en-AU')} contacts.`;
        setNotice(failed > 0 ? `${done} ${failed.toLocaleString('en-AU')} could not be updated; try them again.` : done);
        load();
        loadTags();
      })
      .catch((err) => setError(edmContactErrorMessage(err)))
      .finally(() => setBulkBusy(false));
  };

  // ---- tag registry
  const saveTag = (name: string, kind: EdmTagKind, eventDate: string | null) => {
    setTagBusy(true);
    setTagError(null);
    api
      .saveTag({ name, kind, eventDate })
      .then(loadTags)
      .catch((err) => setTagError(edmContactErrorMessage(err)))
      .finally(() => setTagBusy(false));
  };
  /** Inline create from the contact form: a plain tag, never changing an existing entry. */
  const createTagInline = async (name: string): Promise<string> => {
    const res = await api.saveTag({ name, kind: 'tag', eventDate: null, createOnly: true });
    loadTags();
    return typeof res?.name === 'string' && res.name !== '' ? res.name : name;
  };
  const confirmTagDelete = () => {
    const name = tagDeleteTarget;
    if (!name) return;
    setTagDeleteTarget(null);
    setTagBusy(true);
    setTagError(null);
    api
      .deleteTag(name)
      .then(loadTags)
      .catch((err) => setTagError(edmContactErrorMessage(err)))
      .finally(() => setTagBusy(false));
  };

  if (loading && contacts === null && !error) return <SkeletonTablePage tableRows={6} tableColumns={7} />;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="eDM Contacts"
        subtitle="People you can email with eDM campaigns, with their consent and tags"
        onRefresh={load}
        isRefreshing={loading}
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className={EDM_UI.secondaryButton} onClick={() => setTagsOpen(true)}>
              <Tags className="h-4 w-4" /> Manage tags
            </button>
            <button type="button" className={EDM_UI.primaryButton} onClick={openAdd}>
              <Plus className="h-4 w-4" /> Add contact
            </button>
          </div>
        }
      />

      {notice && <div className={EDM_UI.infoBanner}>{notice}</div>}
      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6"
        role="search"
        aria-label="Filter contacts"
      >
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-q">
            Search
          </label>
          <input
            id="edm-filter-q"
            className={EDM_UI.input}
            placeholder="Email or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-subscribed">
            Subscribed
          </label>
          <select
            id="edm-filter-subscribed"
            className={EDM_UI.input}
            value={filters.subscribed === undefined ? '' : String(filters.subscribed)}
            onChange={(e) => setFilter('subscribed', e.target.value === '' ? undefined : e.target.value === 'true')}
          >
            <option value="">Any</option>
            <option value="true">Subscribed</option>
            <option value="false">Unsubscribed</option>
          </select>
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-status">
            Delivery status
          </label>
          <select
            id="edm-filter-status"
            className={EDM_UI.input}
            value={filters.status ?? ''}
            onChange={(e) => setFilter('status', (e.target.value || undefined) as EdmContactStatusFilter | undefined)}
          >
            <option value="">Any</option>
            <option value="bounced">Bounced</option>
            <option value="complained">Complained</option>
            <option value="suppressed">Suppressed</option>
          </select>
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-tag">
            Tag
          </label>
          <select
            id="edm-filter-tag"
            className={EDM_UI.input}
            value={filters.tag ?? ''}
            onChange={(e) => setFilter('tag', e.target.value || undefined)}
          >
            <option value="">Any</option>
            {contentEnabled
              ? groupTagsByKind(tags).map((g) => (
                  <optgroup key={g.kind} label={TAG_KIND_GROUP_LABEL[g.kind]}>
                    {g.tags.map((t) => (
                      <option key={t.name} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : tags.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
          </select>
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-import">
            Import ID
          </label>
          <input
            id="edm-filter-import"
            className={EDM_UI.input}
            placeholder="Press Enter to apply"
            value={importDraft}
            onChange={(e) => setImportDraft(e.target.value)}
            onBlur={applyImportId}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyImportId();
            }}
          />
        </div>
        <div>
          <label className={EDM_UI.label} htmlFor="edm-filter-broker">
            Broker
          </label>
          <select
            id="edm-filter-broker"
            className={EDM_UI.input}
            value={filters.brokerId ?? ''}
            onChange={(e) => setFilter('brokerId', e.target.value || undefined)}
          >
            <option value="">Any</option>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <BulkTagBar
        selectedCount={selected.size}
        tags={tags}
        busy={bulkBusy}
        onApply={applyBulk}
        onClear={() => setSelected(new Set())}
      />

      {contacts !== null && contacts.length === 0 && !error && (
        <div className={EDM_UI.card}>
          <p className="text-sm text-gray-300">No contacts match. Add one, or import a file from eDM Campaigns.</p>
        </div>
      )}

      {contacts !== null && contacts.length > 0 && (
        <div className="rounded-xl border border-gray-700 bg-gray-800/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all on this page"
                      checked={allOnPage}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Broker</th>
                  <th className="px-4 py-3">Consent</th>
                  <th className="px-4 py-3">Added</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-gray-800">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${c.email}`}
                        checked={selected.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-100">{c.email}</td>
                    <td className="px-4 py-3 text-gray-300">{fullName(c) || '–'}</td>
                    <td className="px-4 py-3 text-gray-300">{c.phone || '–'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags ?? []).map((t) => (
                          <span key={t} className={`${EDM_UI.chip} border-gray-600 text-gray-300`}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{contactStateLabel(c)}</td>
                    <td className="px-4 py-3 text-gray-300">{brokerName(c.brokerId)}</td>
                    <td className="px-4 py-3 text-gray-400">
                      {c.consentSource === 'manual' ? 'Added by hand' : c.consentSource === 'import' ? 'Import' : c.consentSource || '–'}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{formatBrisbaneDateTime(c.createdAt) || '–'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="text-gray-400 hover:text-white"
                          title="Campaign history"
                          aria-label={`History for ${c.email}`}
                          onClick={() => setHistoryContact(c)}
                        >
                          <History className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-white"
                          title="Edit"
                          aria-label={`Edit ${c.email}`}
                          onClick={() => openEdit(c)}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-300"
                          title="Erase"
                          aria-label={`Erase ${c.email}`}
                          onClick={() => setDeleteTarget(c)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {cursorMode ? (
            <CursorPager
              page={page}
              pageSize={pageSize}
              shown={contacts.length}
              total={total}
              hasNext={nextCursor !== null}
              busy={loading}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => {
                if (nextCursor !== null) setPage((p) => p + 1);
              }}
              onPageSizeChange={(n) => {
                setPageSize(Math.min(n, EDM_CONTACTS_MAX_PAGE_SIZE));
                setPage(1);
              }}
            />
          ) : (
            <Pagination
              currentPage={page}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(Math.min(n, EDM_CONTACTS_MAX_PAGE_SIZE));
                setPage(1);
              }}
            />
          )}
        </div>
      )}

      <ContactFormModal
        show={formOpen}
        contact={editing}
        tags={tags}
        brokers={brokers}
        busy={formBusy}
        error={formError}
        onClose={closeForm}
        onAdd={onAdd}
        onUpdate={onUpdate}
        onCreateTag={createTagInline}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Erase contact"
        message={`Erase ${deleteTarget?.email ?? ''}? Their name, phone, tags and consent record are permanently deleted. The address is added to your suppression list for good, so it can never be emailed or added again. Sending records already kept for compliance (7 years) are not changed. This cannot be undone.`}
        confirmLabel="Erase permanently"
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ContactHistoryDrawer
        contact={historyContact}
        loadHistory={api.contactHistory}
        onClose={() => setHistoryContact(null)}
      />

      <Modal title="Manage tags" show={tagsOpen} onClose={() => setTagsOpen(false)}>
        <TagManager
          tags={tags}
          busy={tagBusy}
          error={tagError}
          onSave={saveTag}
          onDelete={(name) => setTagDeleteTarget(name)}
        />
      </Modal>

      <ConfirmDialog
        isOpen={tagDeleteTarget !== null}
        title="Delete tag"
        message={`Delete the tag "${tagDeleteTarget ?? ''}"? It is removed from the tag list only; contacts that have it keep it.`}
        confirmLabel="Delete tag"
        confirmVariant="danger"
        onConfirm={confirmTagDelete}
        onCancel={() => setTagDeleteTarget(null)}
      />
    </div>
  );
};

export default EdmContactsPage;
